import * as React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { BidHistoryItem } from '@/types/BidHistoryItem';
import { getBidHistory } from '@/redux/modules/bidHistory';
import { getCatalog } from '@/redux/modules/catalog';
import { getItem } from '@/redux/modules/item';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

type Props = {
    catalogId: number;
    itemId: number;
    showBidHistory: boolean;
};

const BidHistoryTable = ({ catalogId, itemId, showBidHistory }: Props) => {
    const { currency = 'usd' } = useSelector((state) => getCatalog(state, catalogId), isEqual);
    const bidHistory = useSelector((state) => getBidHistory(state, itemId), isEqual);
    const { startPrice = 1 } = useSelector((state) => getItem(state, itemId), isEqual);

    const [shownBidHistory, setShownBidHistory] = React.useState(
        showBidHistory ? bidHistory : Boolean(bidHistory.length) && bidHistory.slice(0, 3)
    );

    React.useEffect(() => {
        setShownBidHistory(showBidHistory ? bidHistory : bidHistory && bidHistory.slice(0, 3));
    }, [bidHistory, showBidHistory]);

    const hideStartPrice = !showBidHistory && shownBidHistory.length > 2;

    const bidHistoryMapping = React.useCallback(
        (bid: BidHistoryItem, index: number) => {
            return (
                <TableRowDiv key={index}>
                    <TableCellDiv>{bid.source}</TableCellDiv>
                    <TableCellDiv>
                        <FormattedNumber currency={currency} style="currency" value={bid.amount} />
                    </TableCellDiv>
                </TableRowDiv>
            );
        },
        [currency]
    );

    if (shownBidHistory.length > 0) {
        return (
            <div id="bid-history">
                <TableDiv>
                    <BidHistoryDiv>
                        <HeaderRowDiv>
                            <TableCellDiv>
                                <FormattedMessage id="bidder" />
                            </TableCellDiv>
                            <TableCellDiv>
                                <FormattedMessage id="bid" />
                            </TableCellDiv>
                        </HeaderRowDiv>
                        {shownBidHistory.map(bidHistoryMapping)}
                        {!hideStartPrice && (
                            <RowDiv>
                                <TableCellDiv>
                                    <FormattedMessage id="starting_bid" />
                                </TableCellDiv>
                                <TableCellDiv>
                                    <FormattedNumber currency={currency} style="currency" value={startPrice} />
                                </TableCellDiv>
                            </RowDiv>
                        )}
                    </BidHistoryDiv>
                </TableDiv>
            </div>
        );
    }
    return null;
};

export default React.memo<Props>(BidHistoryTable);

const TableRowDiv = styled.div`
    display: table-row;
    border-top: ${({ theme }) => `1px solid ${theme.colors.grey400}`};

    @media print {
        border: none;
    }
`;

const TableCellDiv = styled.div`
    display: table-cell;
    text-transform: uppercase;
    border-right: ${({ theme }) => `1px solid ${theme.colors.grey400}`};
    padding: 13px 15px;

    @media print {
        padding: 0;
        border: none;
    }

    &:last-child {
        border-right: none;
    }
`;

const TableDiv = styled.div`
    flex: 0 0 auto;

    @media print {
        margin-top: 40px;
        max-width: 350px;
        position: absolute;
        top: 600px;
        right: 400px;
        width: 200px;
    }
`;

const BidHistoryDiv = styled.div`
    max-width: 400px;
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
    font-size: 0.8em;
    color: ${({ theme }) => theme.colors.black};
    display: table;
    table-layout: fixed;
    width: 100%;
`;

const HeaderRowDiv = styled.div`
    display: table-row;
    background-color: ${({ theme }) => theme.colors.grey400};
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.grey100};
    border-bottom: ${({ theme }) => `1px solid ${theme.colors.grey400}`};

    @media print {
        border: none;
    }
`;

const RowDiv = styled.div`
    display: table-row;
    border-top: ${({ theme }) => `1px solid ${theme.colors.grey400}`};
    color: ${({ theme }) => theme.colors.grey100};

    @media print {
        border: none;
    }
`;
